import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from './Button'
import { Modal } from './Modal'

export interface ConfirmDeleteModalProps {
  open: boolean
  title: string
  /** Human name of the object, shown in the default body copy. */
  objectName: string
  /** The global unique_id the operator must type to arm the confirm button. */
  expected: string
  /** Body copy above the input — overrides the default "Delete {objectName}?" sentence. */
  children?: ReactNode
  pending: boolean
  /** Mutation error, shown under the input. */
  error?: string | null
  confirmLabel?: string
  onConfirm: () => void
  onClose: () => void
}

/**
 * Type-to-confirm gate for destructive actions (delete/remove/unbind/revoke):
 * the danger button stays disabled until the input exactly matches the
 * object's global unique_id. Extracted from the delete-system modal in
 * SystemDetail — use it for EVERY destructive action in the console.
 */
export function ConfirmDeleteModal({
  open,
  title,
  objectName,
  expected,
  children,
  pending,
  error,
  confirmLabel = 'Delete',
  onConfirm,
  onClose,
}: ConfirmDeleteModalProps) {
  const [confirmText, setConfirmText] = useState('')

  // Clear the typed text each time the modal (re)opens.
  useEffect(() => {
    if (open) setConfirmText('')
  }, [open])

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div className="text-sm text-ostrel-ink">
        {children ?? (
          <p>
            Delete <strong>{objectName}</strong>? This cannot be undone.
          </p>
        )}
      </div>
      <label className="mt-4 block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Type {expected} to confirm
        </span>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={expected}
          autoComplete="off"
          className="tnum mt-1.5 w-full rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 font-mono text-sm text-ostrel-ink focus:border-ostrel-danger focus:outline-none focus:ring-1 focus:ring-ostrel-danger"
        />
      </label>
      {error && <p className="mt-3 text-xs text-ostrel-danger">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={pending || confirmText !== expected}
          onClick={onConfirm}
        >
          {pending ? 'Working…' : confirmLabel}
        </Button>
      </div>
    </Modal>
  )
}
